<script lang="ts" setup>
import { useRoute } from 'vue-router';
import { useOpportunityDetail } from '~/composables/opportunities/useOpportunityDetail';
import { useSiteDetailQuery } from '~/composables/sites/useSitesQuery';
import { useContactDetailQuery } from '~/composables/contacts/useContactsQuery';
import type { BreadcrumbItem } from '~/types/main';

const route = useRoute();
const router = useRouter();

const { opportunity } = useOpportunityDetail();

const siteId = computed(() => route.params.siteId as string | undefined);
const contactId = computed(() => route.params.contactId as string | undefined);

const { site } = useSiteDetailQuery(siteId);
const { contact } = useContactDetailQuery(contactId);

const home = ref({
  label: '',
  icon: 'pi pi-home',
  route: '/',
});

const breadcrumb = computed(() => {
  const items: BreadcrumbItem[] = [];
  const paramsSeen = new Set<string>();
  const routesSeen = new Set<string>();

  const buildBreadcrumbItems = (r: typeof route.matched[number]): BreadcrumbItem[] => {
    const value = r.meta?.breadcrumb;
    const name = r.name?.toString();
    if (!value || routesSeen.has(name)) { return []; }
    routesSeen.add(name);

    return (Array.isArray(value) ? value : [value]).map(label => ({
      label: String(label),
      to: { name: name ?? '', params: route.params },
    }));
  };

  const buildParamItems = (r: typeof route.matched[number]): BreadcrumbItem[] => {
    return Object.entries(route.params).flatMap(([paramName, paramValue]) => {
      if (!paramValue || paramsSeen.has(paramName)) { return []; }
      if (!r.path.includes(`:${paramName}`)) { return []; }
      paramsSeen.add(paramName);

      let label: string = String(paramValue);
      if (paramName === 'opportunityHashId' && opportunity.value) {
        label = opportunity.value.reqId;
      } else if (paramName === 'siteId' && site.value?.name) {
        label = site.value.name;
      } else if (paramName === 'contactId' && contact.value) {
        label = [contact.value.firstName, contact.value.lastName].filter(Boolean).join(' ') || label;
      }

      return [{
        label,
        to: { name: r.name?.toString() ?? '', params: route.params },
      }];
    });
  };

  for (const referrerRoute of route.matched) {
    const foundRoute = router.getRoutes().find(r => r.path === referrerRoute.path);
    items.push(...buildBreadcrumbItems(foundRoute));
    items.push(...buildParamItems(foundRoute));
  }

  return items;
});
</script>

<template>
  <div
    v-if="!route.params.opportunityHashId || opportunity"
    class="border-b border-t-0 border-x-0 border-solid border-surface"
  >
    <Breadcrumb
      :home="home"
      :model="breadcrumb"
    >
      <template #item="{ item, props }">
        <NuxtLink
          :to="item.to"
          class="cursor-pointer"
          v-bind="props.action"
        >
          <span
            v-if="item.icon"
            :class="[item.icon, 'text-color']"
          />
          <span class="text-surface-700 dark:text-surface-0">{{ item.label }}</span>
        </NuxtLink>
      </template>
      <template #separator>
        <svg
          fill="none"
          height="21"
          viewBox="0 0 12 37"
          width="6.81"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 1L11 18.5L1 36"
            stroke="#E0E0E0"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </template>
    </Breadcrumb>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-breadcrumb) {
  height: 21px;
  padding: 0 0 0 1rem;
  .p-breadcrumb-list {
    height: 100%;
  }
  .p-breadcrumb-item-icon {
    color: red;
  }
}
</style>
